/**
 * Metpure Product Media Swiper Web Component
 * Luxury Layout: Vertical Left Thumbnails + Large Hero Display (Desktop)
 * Touch Swiper Carousel (Mobile)
 * Includes Variant Synchronization and Live Counter
 */

if (!customElements.get('product-media-swiper')) {
  class ProductMediaSwiper extends HTMLElement {
    constructor() {
      super();
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.initTimer = null;
      this.resizeHandler = null;
      this.isDesktop = window.innerWidth >= 990;
      this.mediaIndexMap = {};
      this.variantsMap = {};
    }

    connectedCallback() {
      this.buildMediaIndexMap();
      this.waitForSwiper(() => {
        this.initSwipers();
        this.initResizeListener();
        this.initLightbox();
      });
      this.initVariantListeners();
      this.initThemeEditorListeners();
    }

    disconnectedCallback() {
      if (this.initTimer) clearInterval(this.initTimer);
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      this.destroySwipers();
    }

    buildMediaIndexMap() {
      this.mediaIndexMap = {};
      const slides = this.querySelectorAll('.product-media-swiper-main .swiper-slide');
      slides.forEach((slide, index) => {
        const mediaId = slide.dataset.mediaId;
        if (mediaId) {
          this.mediaIndexMap[String(mediaId)] = index;
        }
        const mediaSrc = slide.dataset.mediaSrc;
        if (mediaSrc) {
          const filename = mediaSrc.split('/').pop().split('?')[0];
          this.mediaIndexMap[filename] = index;
        }
      });

      // Parse variants JSON
      try {
        const jsonEl = this.querySelector('.product-variants-json');
        if (jsonEl && jsonEl.textContent) {
          const variants = JSON.parse(jsonEl.textContent);
          this.variantsMap = {};
          variants.forEach((v) => {
            this.variantsMap[String(v.id)] = v;
          });
        }
      } catch (err) {
        console.error('ProductMediaSwiper: Error parsing variants JSON', err);
      }
    }

    waitForSwiper(callback) {
      if (typeof window.Swiper !== 'undefined') {
        callback();
        return;
      }

      let attempts = 0;
      const maxAttempts = 60; // 6 seconds
      this.initTimer = setInterval(() => {
        attempts++;
        if (typeof window.Swiper !== 'undefined') {
          clearInterval(this.initTimer);
          callback();
        } else if (attempts >= maxAttempts) {
          clearInterval(this.initTimer);
          console.warn('ProductMediaSwiper: Swiper library not detected.');
        }
      }, 100);
    }

    initSwipers() {
      const mainContainer = this.querySelector('.product-media-swiper-main');
      const thumbsContainer = this.querySelector('.product-media-swiper-thumbs');

      if (!mainContainer) return;

      this.destroySwipers();

      const isDesktop = window.innerWidth >= 990;
      this.isDesktop = isDesktop;

      // 1. Initialize Thumbs Swiper (Horizontal below main image)
      if (thumbsContainer) {
        this.thumbsSwiper = new window.Swiper(thumbsContainer, {
          direction: 'horizontal',
          slidesPerView: 5,
          spaceBetween: 12,
          freeMode: false,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          centerInsufficientSlides: false,
          observer: true,
          observeParents: true,
          // mouse wheel / trackpad scrolls the strip; at either end the page scrolls as normal
          mousewheel: {
            forceToAxis: false,
            releaseOnEdges: true,
          },
          breakpoints: {
            0: {
              slidesPerView: 4.2,
              spaceBetween: 8,
            },
            640: {
              slidesPerView: 4.5,
              spaceBetween: 10,
            },
            990: {
              slidesPerView: 5,
              spaceBetween: 12,
            },
          },
        });
      }

      // 2. Initialize Main Swiper
      const mainConfig = {
        slidesPerView: 1,
        spaceBetween: 16,
        speed: 350,
        grabCursor: true,
        autoHeight: true,
        observer: true,
        observeParents: true,
      };

      if (this.thumbsSwiper) {
        mainConfig.thumbs = {
          swiper: this.thumbsSwiper,
        };
      }

      this.mainSwiper = new window.Swiper(mainContainer, mainConfig);

      if (this.hasAttribute('data-lightbox')) {
        // Swiper's click fires only for a real tap/click, not at the end of a swipe
        this.mainSwiper.on('click', (swiper, event) => {
          const slide = swiper.clickedSlide;
          if (!slide || slide.dataset.lightboxIndex == null) return; // videos keep playing inline
          if (event && event.target && event.target.closest('iframe, video, button, a')) return;
          this.openLightbox(Number(slide.dataset.lightboxIndex));
        });
        this.mainSwiper.on('slideChange', () => this.updateExpandButton());
        this.updateExpandButton();
      }
    }

    // ---- Full-screen photo viewer (only photos; videos stay inline in the gallery) ----

    initLightbox() {
      if (this.lightboxReady || !this.hasAttribute('data-lightbox')) return;
      this.lightbox = this.querySelector('.pms-lightbox');
      if (!this.lightbox) return;
      this.lightboxReady = true;
      this.expandButton = this.querySelector('[data-lightbox-open]');
      this.counter = this.lightbox.querySelector('.pms-lightbox__counter');

      if (this.expandButton) {
        this.expandButton.addEventListener('click', () => {
          const slide = this.mainSwiper && this.mainSwiper.slides[this.mainSwiper.activeIndex];
          this.openLightbox(slide && slide.dataset.lightboxIndex != null ? Number(slide.dataset.lightboxIndex) : 0);
        });
      }
      this.lightbox.querySelector('.pms-lightbox__close').addEventListener('click', () => this.lightbox.close());
      this.lightbox.addEventListener('close', () => this.onLightboxClose());
      this.updateExpandButton();
    }

    updateExpandButton() {
      if (!this.expandButton || !this.mainSwiper) return;
      const slide = this.mainSwiper.slides[this.mainSwiper.activeIndex];
      this.expandButton.hidden = !(slide && slide.dataset.lightboxIndex != null);
    }

    openLightbox(index) {
      if (!this.lightbox || this.lightbox.open) return;
      this.lightboxOpener = document.activeElement;
      this.lightbox.showModal();
      document.documentElement.classList.add('pms-lightbox-open');

      if (!this.lightboxSwiper) {
        // Mouse: one click zooms in/out at the point clicked. Touch: pinch, or double-tap (Swiper zoom toggle).
        this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        const lb = this.lightbox;
        this.lightboxSwiper = new window.Swiper(lb.querySelector('.pms-lightbox__swiper'), {
          initialSlide: index,
          spaceBetween: 24,
          speed: 300,
          zoom: { maxRatio: 3, toggle: !this.finePointer },
          keyboard: { enabled: false },
          navigation: lb.querySelector('.pms-lightbox__next')
            ? { nextEl: lb.querySelector('.pms-lightbox__next'), prevEl: lb.querySelector('.pms-lightbox__prev') }
            : false,
          on: {
            slideChange: () => this.onLightboxSlide(),
            click: (swiper, event) => {
              const onImage = event && event.target && event.target.closest('.pms-lightbox__image');
              if (!onImage) {
                lb.close(); // tap/click on the dark area around the photo closes the viewer
              } else if (this.finePointer) {
                swiper.zoom.toggle(event);
              }
            },
          },
        });
      } else {
        this.lightboxSwiper.update();
        this.lightboxSwiper.slideTo(index, 0);
      }
      this.lightboxSwiper.keyboard.enable();
      this.onLightboxSlide();
      this.lightbox.querySelector('.pms-lightbox__close').focus({ preventScroll: true });
    }

    onLightboxSlide() {
      const swiper = this.lightboxSwiper;
      if (!swiper) return;
      const total = swiper.slides.length;
      if (this.counter) this.counter.textContent = total > 1 ? swiper.activeIndex + 1 + ' / ' + total : '';
      // load the photo shown and its neighbours right away (they are lazy until the viewer opens)
      [swiper.activeIndex - 1, swiper.activeIndex, swiper.activeIndex + 1].forEach((i) => {
        const img = swiper.slides[i] && swiper.slides[i].querySelector('img');
        if (img) img.loading = 'eager';
      });
    }

    onLightboxClose() {
      document.documentElement.classList.remove('pms-lightbox-open');
      const swiper = this.lightboxSwiper;
      if (swiper) {
        if (swiper.zoom && swiper.zoom.scale > 1) swiper.zoom.out();
        swiper.keyboard.disable();
        // the gallery follows to the photo last viewed
        const slide = swiper.slides[swiper.activeIndex];
        if (slide && this.mainSwiper) this.mainSwiper.slideTo(Number(slide.dataset.mainIndex), 0);
      }
      if (this.lightboxOpener && typeof this.lightboxOpener.focus === 'function') this.lightboxOpener.focus({ preventScroll: true });
    }

    initResizeListener() {
      let resizeTimer = null;
      this.resizeHandler = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const nowDesktop = window.innerWidth >= 990;
          if (nowDesktop !== this.isDesktop) {
            this.initSwipers();
          }
        }, 150);
      };
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    }

    destroySwipers() {
      if (this.mainSwiper && typeof this.mainSwiper.destroy === 'function') {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      if (this.thumbsSwiper && typeof this.thumbsSwiper.destroy === 'function') {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }
    }

    initVariantListeners() {
      // 1. Listen for global PubSub if available
      try {
        const pubSub = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
        const subFn = window.subscribe || (typeof subscribe === 'function' ? subscribe : null);
        if (subFn && pubSub && pubSub.variantChange) {
          subFn(pubSub.variantChange, (event) => {
            if (event && event.data && event.data.variant) {
              this.handleVariantChange(event.data.variant);
            }
          });
        }
      } catch (e) {
        // Fallback to DOM events
      }

      // 2. Custom event listener on document
      document.addEventListener('variant:change', (event) => {
        if (event && event.detail && event.detail.variant) {
          this.handleVariantChange(event.detail.variant);
        }
      });

      // 3. Form change listener
      const productForm = document.querySelector(`form[action*="/cart/add"][id*="${this.dataset.sectionId}"]`);
      if (productForm) {
        productForm.addEventListener('change', (event) => {
          if (event.target.name === 'id') {
            const variantId = event.target.value;
            const variant = this.variantsMap[String(variantId)];
            if (variant) {
              this.handleVariantChange(variant);
            }
          }
        });
      }
    }

    handleVariantChange(variant) {
      if (!variant) return;

      let targetIndex = undefined;

      // 1. Check featured media id
      if (variant.featured_media && variant.featured_media.id) {
        const mediaId = String(variant.featured_media.id);
        targetIndex = this.mediaIndexMap[mediaId];
      }

      // 2. Fallback to image src filename matching
      if (typeof targetIndex === 'undefined' && variant.featured_image && variant.featured_image.src) {
        const filename = variant.featured_image.src.split('/').pop().split('?')[0];
        targetIndex = this.mediaIndexMap[filename];
      }

      if (typeof targetIndex !== 'undefined' && this.mainSwiper) {
        this.mainSwiper.slideTo(targetIndex, 400);
      }
    }

    initThemeEditorListeners() {
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:load', (e) => {
          if (e.detail.sectionId === this.dataset.sectionId) {
            this.buildMediaIndexMap();
            this.initSwipers();
          }
        });
      }
    }
  }

  customElements.define('product-media-swiper', ProductMediaSwiper);
}
